import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDocumentFlexDataLinks } from 'pages/flex-data/flex-data-links/useDocumentFlexDataLinks';
import { useLinkCrmObject } from 'pages/flex-data/flex-data-links/useLinkCrmObject';
import type { FlexDataLink, RelatedLink } from 'pages/flex-data/flex-data-links/types';
import { CrmObjectAutocomplete } from './CrmObjectAutocomplete';
import { CrmObjectsList } from './CrmObjectsList';

interface LinkCrmObjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  onLinked?: (relatedLinks: RelatedLink[]) => void;
}

export function LinkCrmObjectModal({ isOpen, onClose, contentId, onLinked }: LinkCrmObjectModalProps) {
  const { t } = useTranslation();
  const [stagedObjects, setStagedObjects] = useState<FlexDataLink[]>([]);

  const { linkedObjects, isLoading: isLinkedObjectsLoading } = useDocumentFlexDataLinks(contentId);
  const { linkObject } = useLinkCrmObject(contentId, onLinked);

  const crmLinkedObjects = useMemo(() => linkedObjects.filter((o) => o.dataSourceType !== 'Proposify'), [linkedObjects]);

  const toObjectId = (o: FlexDataLink) => `${o.dataSourceType}|${o.tenantId}|${o.objectId}`;

  const excludedObjectIds = useMemo(
    () => new Set([...stagedObjects.map(toObjectId), ...crmLinkedObjects.map(toObjectId)]),
    [stagedObjects, crmLinkedObjects]
  );

  const excludedNormalizedTypes = useMemo(
    () => new Set([...stagedObjects.map((o) => o.normalizedType), ...crmLinkedObjects.map((o) => o.normalizedType)]),
    [stagedObjects, crmLinkedObjects]
  );

  const handleClose = () => {
    setStagedObjects([]);
    onClose();
  };

  const handleSelect = (selected: FlexDataLink) => {
    setStagedObjects((prev) => [selected, ...prev]);
  };

  const handleRemoveStaged = (objectId: string) => {
    setStagedObjects((prev) => prev.filter((o) => o.objectId !== objectId));
  };

  const handleLinkToDocument = () => {
    stagedObjects.forEach((obj) => linkObject(obj));
    handleClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="column">
            <Typography variant="h6">{t('editor.side_panel.link_crm_object_modal.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('editor.side_panel.link_crm_object_modal.subtitle')}
            </Typography>
          </Stack>
          <IconButton aria-label="close" data-testid="link-crm-object-modal-close" onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: '6px !important', minHeight: 165 }}>
        <Stack direction="column" gap={1.5}>
          <CrmObjectAutocomplete
            excludedObjectIds={excludedObjectIds}
            excludedNormalizedTypes={excludedNormalizedTypes}
            onSelect={handleSelect}
            disabled={isLinkedObjectsLoading}
          />
          <CrmObjectsList
            stagedObjects={stagedObjects}
            linkedObjects={crmLinkedObjects}
            contentId={contentId}
            onRemoveStaged={handleRemoveStaged}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" disableElevation onClick={handleLinkToDocument}>
          {t('editor.side_panel.link_crm_object_modal.link_to_document')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
